import { forwardRef, useEffect, useState } from "react";
import * as ThemeUI from "theme-ui";

import type { lightTheme } from "@theme/themes/ms5/theme";
import type * as badgeVariants from "@theme/themes/ms5/variants/badges";
import type * as buttonVariants from "@theme/themes/ms5/variants/buttons";
import type * as imageVariants from "@theme/themes/ms5/variants/images";
import type * as layoutVariants from "@theme/themes/ms5/variants/layout";
import type * as linkVariants from "@theme/themes/ms5/variants/links";
import type * as textVariants from "@theme/themes/ms5/variants/text";
import type { SyntheticEvent } from "react";

export type ThemeUIStyleObject = ThemeUI.ThemeUIStyleObject;
export type ThemeUICSSObject = ThemeUI.ThemeUICSSObject;

export type Theme = typeof lightTheme;

/**
 * Get all the nested keys of a theme object.
 *
 * This is useful for getting the keys of a theme object to use with getThemeValue.
 * it will return a string with the nested keys separated by a dot.
 * if a value is an array or string it will stop being recursive.
 */
type NestedKeys<T> = {
  [K in keyof T]: K extends string
    ? T[K] extends readonly unknown[]
      ? `${K}`
      : T[K] extends object
        ? `${K}` | `${K}.${NestedKeys<T[K]>}`
        : `${K}`
    : never;
}[keyof T];

export type ThemeKeys = NestedKeys<Theme>;

/**
 * Get Theme Value
 *
 * This is a wrapper around the theme-ui get function that allows us to use our typed theme.
 */
export const getThemeValue = ThemeUI.get as (
  themeObject: object,
  path: ThemeKeys,
  fallback?: unknown,
  p?: number,
  undef?: unknown,
) => string;

// Overriding Theme UI types since our theme values are typed.
interface ThemeUIContext extends Omit<ThemeUI.ThemeUIContextValue, "theme"> {
  theme: Theme;
}

export const useThemeUI = ThemeUI.useThemeUI as () => ThemeUIContext;

export const jsx = ThemeUI.jsx;
export const ThemeUIProvider = ThemeUI.ThemeUIProvider;

export const Box = ThemeUI.Box;
export const Grid = ThemeUI.Grid;
export const Flex = ThemeUI.Flex;

export const Close = ThemeUI.Close;
export const Divider = ThemeUI.Divider;
export const Donut = ThemeUI.Donut;
export const Spinner = ThemeUI.Spinner;

export const AspectRatio = ThemeUI.AspectRatio;
export const AspectImage = ThemeUI.AspectImage;

export const Input = ThemeUI.Input;
export const Label = ThemeUI.Label;

export const Textarea = ThemeUI.Textarea;

/**
 * Theme Ui  Link component.
 *
 * Link style variants can be defined in the theme/variants/links.ts.
 * @see https://theme-ui.com/components/link
 */
export interface LinkProps extends ThemeUI.LinkProps {
  /** Variants pulled directly from the link variants in our theme */
  variant?: keyof typeof linkVariants;
}
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <ThemeUI.Link {...props} ref={ref} />
));

Link.displayName = "Link";

/**
 * Theme Ui button component.
 *
 * Button style variants can be defined in the theme/variants/buttons.ts.
 * @see https://theme-ui.com/components/button
 */
export interface ButtonProps extends ThemeUI.ButtonProps {
  /** Variants pulled directly from the button variants in our theme */
  variant?: keyof typeof buttonVariants;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <ThemeUI.Button {...props} ref={ref} />
));

Button.displayName = "Button";

/**
 * Theme Ui  icon button component.
 *
 * Button style variants can be defined in the theme/variants/buttons.ts.
 * @see https://theme-ui.com/components/icon-button
 */
export interface IconButtonProps extends ThemeUI.IconButtonProps {
  /** Variants pulled directly from the button variants in our theme */
  variant?: keyof typeof buttonVariants;
}
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => (
  <ThemeUI.IconButton {...props} ref={ref} />
));

IconButton.displayName = "IconButton";

/**
 * Theme Ui  layout component.
 *
 * Layout style variants can be defined in the theme/variants/layout.ts.
 * @see https://theme-ui.com/components/container
 */
export interface ContainerProps extends ThemeUI.ContainerProps {
  /** Variants pulled directly from the layout variants in our theme */
  variant?: keyof typeof layoutVariants;
}

export const Container = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => (
  <ThemeUI.Container {...props} ref={ref} />
));

Container.displayName = "Container";

/**
 * Theme Ui  typographic component.
 *
 * Heading style variants can be defined in the theme/variants/text.ts.
 * @see https://theme-ui.com/components/heading
 */
export interface HeadingProps extends ThemeUI.HeadingProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof textVariants;
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>((props, ref) => (
  <ThemeUI.Heading {...props} ref={ref} />
));

Heading.displayName = "Heading";

/**
 * Theme Ui  typographic component.
 *
 * Text style variants can be defined in the theme/variants/text.ts.
 * @see https://theme-ui.com/components/text
 */
export interface TextProps extends ThemeUI.TextProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof textVariants;
}

export const Text = forwardRef<HTMLDivElement, TextProps>((props, ref) => (
  <ThemeUI.Text as={props.as ?? "span"} {...props} ref={ref} />
));

Text.displayName = "Text";

/**
 * Theme Ui  typographic component.
 *
 * Text style variants can be defined in the theme/variants/text.ts.
 * @see https://theme-ui.com/components/text
 */
export interface ParagraphProps extends ThemeUI.TextProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof textVariants;
}

export const Paragraph = forwardRef<HTMLParagraphElement, ParagraphProps>((props, ref) => (
  <ThemeUI.Text as={props.as ?? "p"} {...props} ref={ref} />
));

Paragraph.displayName = "Paragraph";

/**
 * Theme Ui  Image component.
 *
 * Updated to default to loading="lazy"
 *
 * @see https://theme-ui.com/components/image
 */
export interface ImageProps extends ThemeUI.ImageProps {
  /** Variants pulled directly from the text variants in our theme */
  variant?: keyof typeof imageVariants;
}

const MAX_SVG_RETRIES = 2;

/**
 * Static SVGs are served as individual files from `/svg/`, and every themed Image defaults to
 * lazy loading. Scrolling quickly brings many into the viewport at once, so they are requested
 * as a single burst over one connection — a momentary drop takes out everything in flight and
 * each one renders as a broken-image placeholder. Retrying recovers them; the `?r=` suffix is
 * what forces a fresh request rather than a replay of the failed one.
 */
const isRetryableSvgSrc = (src: ThemeUI.ImageProps["src"]): src is string =>
  typeof src === "string" && src.startsWith("/svg/");

/**
 * `alt=""` is the platform's signal that an image carries no information, so removing it costs a
 * reader nothing. Anything else — including a missing `alt`, which means the caller forgot rather
 * than opted out — keeps the failed element, whose `alt` a screen reader still announces.
 */
const isDecorative = (alt: ImageProps["alt"]) => alt === "";

export const Image = forwardRef<HTMLImageElement, ImageProps>(
  ({ src, alt, loading, onError, ...rest }, ref) => {
    const [retryCount, setRetryCount] = useState(0);
    const [hasExhaustedRetries, setHasExhaustedRetries] = useState(false);

    useEffect(() => {
      setRetryCount(0);
      setHasExhaustedRetries(false);
    }, [src]);

    if (hasExhaustedRetries && isDecorative(alt)) return null;

    const handleError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
      if (isRetryableSvgSrc(src)) {
        if (retryCount < MAX_SVG_RETRIES) {
          setRetryCount((count) => count + 1);
        } else {
          console.warn(`[Image] giving up on ${src} after ${MAX_SVG_RETRIES} retries`);
          setHasExhaustedRetries(true);
        }
      }

      onError?.(event);
    };

    return (
      <ThemeUI.Image
        {...rest}
        alt={alt}
        src={retryCount > 0 ? `${src}?r=${retryCount}` : src}
        loading={loading ?? "lazy"}
        onError={handleError}
        ref={ref}
      />
    );
  },
);

Image.displayName = "Image";

export interface BadgeProps extends ThemeUI.BadgeProps {
  /** Variants pulled directly from the badge variants in our theme */
  variant?: keyof typeof badgeVariants;
}
export const Badge = forwardRef<HTMLDivElement, BadgeProps>((props, ref) => (
  <ThemeUI.Badge {...props} ref={ref} />
));
Badge.displayName = "Badge";
